/**
 * コピペの箱判定と、クリップボード座標の変換。
 * 設置はコピーした物体全体の AABB を一つの箱として面に揃える。
 */

import type { BlockPose } from './block-lattice';
import { normalizeObjectYaw, yawFacingXZ } from './object-pose';
import { shouldSitUpright } from './object-snap';
import {
  colliderBounds,
  entityPickColliders,
  entityPickOverlapsPose,
  poseVolume,
  rangeDestroyBox,
  unionBounds,
  type Aabb,
} from './physics';
import { mapClothBake } from './cloth';
import type {
  BraceEntity,
  BraceGeometry,
  Entity,
  ObjectEntity,
  ObjectGeometry,
  StairEntity,
  StairGeometry,
  Vec3,
} from './types';
import { validateEntity } from './validation';
import { localToWorldXZ, worldToLocalXZ } from './view-grid';

const OVERLAP_EPS = 1e-4;

/** クリップボードに残せる entity 数の上限 */
export const MAX_CLIPBOARD_ITEMS = 512;

type StripMeta<T> = Omit<T, 'id' | 'ownerId' | 'createdAt' | 'updatedAt'>;
export type ClipboardItem = StripMeta<BraceEntity> | StripMeta<StairEntity> | StripMeta<ObjectEntity>;

export function copyRegionBox(start: Vec3, end: Vec3, yaw: number): BlockPose {
  return rangeDestroyBox(start, end, yaw);
}

export function copyRegionVolume(start: Vec3, end: Vec3, yaw: number): number {
  return poseVolume(rangeDestroyBox(start, end, yaw));
}

export function entityHitsCopyBox(entity: Entity, pose: BlockPose): boolean {
  return entityPickOverlapsPose(entity, pose);
}

export function toClipboardItems(
  entities: readonly Entity[],
  origin: Vec3,
  yaw: number
): ClipboardItem[] {
  const mapPoint = (point: Vec3) => worldToCopyLocal(point, origin, yaw);
  const mapVec = (value: Vec3) => rotateYaw(value, -yaw);
  return entities.map((entity) => transformClipboardItem(stripEntity(entity), mapPoint, mapVec, -yaw));
}

export function fromClipboardItems(
  items: readonly ClipboardItem[],
  origin: Vec3,
  yaw: number
): ClipboardItem[] {
  const mapPoint = (point: Vec3) => copyLocalToWorld(point, origin, yaw);
  const mapVec = (value: Vec3) => rotateYaw(value, yaw);
  return items.map((item) => transformClipboardItem(item, mapPoint, mapVec, yaw));
}

export interface PlaceClipboardInput {
  items: readonly ClipboardItem[];
  hit: Vec3;
  yaw: number;
  normal: Vec3;
  cameraY: number;
}

/** コピーした物体の視線ヒット AABB を、クリップボード局所座標でまとめる。 */
export function clipboardItemsAabb(items: readonly ClipboardItem[]): Aabb | null {
  const boxes: Aabb[] = [];
  for (const [index, item] of items.entries()) {
    const entity = clipboardItemToEntity(item, `bounds-${index}`);
    for (const collider of entityPickColliders(entity)) {
      boxes.push(colliderBounds(collider));
    }
  }
  return unionBounds(boxes);
}

/**
 * AABB の接触面をヒット点に合わせる。床は底面、天井は上面。
 * 壁は視点の上下で座り／吊りを選び、箱全体を部屋側へ出す。
 */
export function placeClipboardItems(input: PlaceClipboardInput): ClipboardItem[] {
  return fromClipboardItems(input.items, clipboardPlaceOrigin(input), input.yaw);
}

/** 視線の水平向きを Y まわり 0° / 90° / 180° / 270° に丸める。 */
export function clipboardYawFromLook(look: Vec3): number {
  return yawFacingXZ(look);
}

export function clipboardPlaceOrigin(input: PlaceClipboardInput): Vec3 {
  const aabb = clipboardItemsAabb(input.items);
  if (!aabb) return { ...input.hit };
  const sit = shouldSitUpright(input.normal, input.cameraY, input.hit.y);
  const contact = rotateYaw(
    {
      x: (aabb.min.x + aabb.max.x) * 0.5,
      y: sit ? aabb.min.y : aabb.max.y,
      z: (aabb.min.z + aabb.max.z) * 0.5,
    },
    input.yaw
  );
  return flushClipboardAabb(
    {
      x: input.hit.x - contact.x,
      y: input.hit.y - contact.y,
      z: input.hit.z - contact.z,
    },
    aabb,
    input.yaw,
    input.hit,
    input.normal
  );
}

function flushClipboardAabb(
  origin: Vec3,
  aabb: Aabb,
  yaw: number,
  hit: Vec3,
  outward: Vec3
): Vec3 {
  const len = Math.hypot(outward.x, outward.y, outward.z);
  if (len < 1e-9) return origin;
  const n = { x: outward.x / len, y: outward.y / len, z: outward.z / len };
  let minDot = Infinity;
  for (const x of [aabb.min.x, aabb.max.x]) {
    for (const y of [aabb.min.y, aabb.max.y]) {
      for (const z of [aabb.min.z, aabb.max.z]) {
        const world = copyLocalToWorld({ x, y, z }, origin, yaw);
        const d =
          (world.x - hit.x) * n.x + (world.y - hit.y) * n.y + (world.z - hit.z) * n.z;
        if (d < minDot) minDot = d;
      }
    }
  }
  if (minDot >= -OVERLAP_EPS) return origin;
  return {
    x: origin.x - n.x * minDot,
    y: origin.y - n.y * minDot,
    z: origin.z - n.z * minDot,
  };
}

export function parseClipboardItems(value: unknown): ClipboardItem[] | null {
  if (!Array.isArray(value) || value.length === 0 || value.length > MAX_CLIPBOARD_ITEMS) return null;
  const items: ClipboardItem[] = [];
  for (const raw of value) {
    const parsed = validateEntity({
      ...(typeof raw === 'object' && raw !== null ? raw : {}),
      id: 'clipboard',
    });
    if (!parsed.ok) return null;
    items.push(stripEntity(parsed.value));
  }
  return items;
}

export function clipboardItemToEntity(item: ClipboardItem, id: string): Entity {
  if (item.type === 'object') {
    return {
      id,
      type: 'object',
      definitionId: item.definitionId,
      material: item.material,
      geometry: cloneObjectGeometry(item.geometry),
      ...(item.components ? { components: item.components } : {}),
    };
  }
  if (item.type === 'stair') {
    return {
      id,
      type: 'stair',
      material: item.material,
      geometry: cloneStairGeometry(item.geometry),
      ...(item.components ? { components: item.components } : {}),
    };
  }
  return {
    id,
    type: 'brace',
    material: item.material,
    geometry: cloneBraceGeometry(item.geometry),
    ...(item.components ? { components: item.components } : {}),
  };
}

export function stripEntity(entity: Entity): ClipboardItem {
  if (entity.type === 'object') {
    return {
      type: 'object',
      definitionId: entity.definitionId,
      material: entity.material,
      geometry: cloneObjectGeometry(entity.geometry),
      ...(entity.components ? { components: entity.components } : {}),
    };
  }
  if (entity.type === 'stair') {
    return {
      type: 'stair',
      material: entity.material,
      geometry: cloneStairGeometry(entity.geometry),
      ...(entity.components ? { components: entity.components } : {}),
    };
  }
  return {
    type: 'brace',
    material: entity.material,
    geometry: cloneBraceGeometry(entity.geometry),
    ...(entity.components ? { components: entity.components } : {}),
  };
}

function worldToCopyLocal(point: Vec3, origin: Vec3, yaw: number): Vec3 {
  const xz = worldToLocalXZ(point.x - origin.x, point.z - origin.z, yaw);
  return { x: xz.x, y: point.y - origin.y, z: xz.z };
}

function copyLocalToWorld(point: Vec3, origin: Vec3, yaw: number): Vec3 {
  const xz = localToWorldXZ(point.x, point.z, yaw);
  return { x: origin.x + xz.x, y: origin.y + point.y, z: origin.z + xz.z };
}

function rotateYaw(value: Vec3, yaw: number): Vec3 {
  const xz = localToWorldXZ(value.x, value.z, yaw);
  return { x: xz.x, y: value.y, z: xz.z };
}

function transformClipboardItem(
  item: ClipboardItem,
  mapPoint: (point: Vec3) => Vec3,
  mapVec: (value: Vec3) => Vec3,
  deltaYaw: number
): ClipboardItem {
  if (item.type === 'object') {
    return {
      ...item,
      geometry: {
        ...cloneObjectGeometry(item.geometry),
        origin: mapPoint(item.geometry.origin),
        yaw: normalizeObjectYaw(item.geometry.yaw + deltaYaw),
      },
    };
  }
  if (item.type === 'stair') {
    return {
      ...item,
      geometry: {
        start: mapPoint(item.geometry.start),
        end: mapPoint(item.geometry.end),
        ...(item.geometry.width !== undefined ? { width: item.geometry.width } : {}),
      },
    };
  }
  return {
    ...item,
    geometry: mapBraceGeometry(item.geometry, mapPoint, mapVec),
  };
}

function mapBraceGeometry(
  geometry: BraceGeometry,
  mapPoint: (point: Vec3) => Vec3,
  mapVec: (value: Vec3) => Vec3
): BraceGeometry {
  const next: BraceGeometry = {
    start: mapPoint(geometry.start),
    end: mapPoint(geometry.end),
    thickness: geometry.thickness,
    rotation: geometry.rotation,
  };
  if (geometry.points) next.points = geometry.points.map(mapPoint);
  if (geometry.closed) next.closed = true;
  if (geometry.fill) next.fill = true;
  if (geometry.arcs) {
    next.arcs = geometry.arcs.map((arc) => ({ segment: arc.segment, center: mapPoint(arc.center) }));
  }
  if (geometry.extrude) next.extrude = mapVec(geometry.extrude);
  if (geometry.normal) next.normal = mapVec(geometry.normal);
  if (geometry.capHoles) next.capHoles = geometry.capHoles.map((hole) => hole.map(mapPoint));
  if (geometry.holes) {
    next.holes = geometry.holes.map((hole) => ({
      segment: hole.segment,
      uv: hole.uv.map((uv) => ({ ...uv })),
    }));
  }
  if (geometry.cloth) next.cloth = mapClothBake(geometry.cloth, mapPoint, mapVec);
  if (geometry.revolve) next.revolve = { axis: geometry.revolve.axis };
  return next;
}

function cloneBraceGeometry(geometry: BraceGeometry): BraceGeometry {
  return mapBraceGeometry(geometry, (point) => ({ ...point }), (value) => ({ ...value }));
}

function cloneStairGeometry(geometry: StairGeometry): StairGeometry {
  return {
    start: { ...geometry.start },
    end: { ...geometry.end },
    ...(geometry.width !== undefined ? { width: geometry.width } : {}),
  };
}

function cloneObjectGeometry(geometry: ObjectGeometry): ObjectGeometry {
  return {
    origin: { ...geometry.origin },
    yaw: geometry.yaw,
    ...(geometry.mount ? { mount: geometry.mount } : {}),
  };
}
